import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { FutureCapabilityModal } from "@/components/FutureCapabilityModal";
import { FutureCapability } from "@/lib/sprint2Entities";
import { Plug, PlugZap } from "lucide-react";
import { toast } from "sonner";

export default function IntegrationsPage() {
  const nav = useNavigate();
  // RC4 · Master Tour integration removed from the UI. No outbound
  // calls, no configuration modal, no demo-import surface. Historical
  // mt_* Mongo documents (if any) remain readable but are not written
  // by any active code path.
  const [openQb, setOpenQb] = useState(false);

  return (
    <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Integrations</SectionHeader>
      <PageHeader testid="page-integrations" title="Integrations"
        backTo={{ label: "Back to Administration", to: "/administration" }}
        subtitle="External systems and API connections" />
      <div className="grid md:grid-cols-2 gap-4">
        <Card
          title="QuickBooks Online"
          badge={<StatusBadge variant="off">Not Connected</StatusBadge>}
          description="Production accounting sync. Locked off in this build — no outbound HTTP requests are made from any code path."
          testid="integration-quickbooks"
          actions={[
            { label: "Connect QuickBooks", onClick: () => setOpenQb(true), primary: true, testid: "qb-connect" },
          ]}
        />

        <Card
          title="Motive"
          badge={<StatusBadge variant="demo">Demo · Read-Only</StatusBadge>}
          description="ELD, GPS, telemetry, HOS, DVIR, diagnostics, fuel, IFTA, and geofence data. Consumed READ-ONLY — ALL ACCESS COACH LEASING never modifies Motive records."
          testid="integration-motive"
          actions={[
            { label: "Open Capability Catalog", onClick: () => nav("/administration/integrations/motive"), primary: true, testid: "motive-open-catalog" },
          ]}
        />
      </div>

      <FutureCapabilityModal open={openQb} onOpenChange={setOpenQb}
        title="QuickBooks Online — Production Capability"
        body={FutureCapability.quickbooks} />
    </PageShell>
  );
}

const Card = ({ title, badge, description, actions, testid }) => (
  <div className="bg-white border border-coach-silver rounded-md p-5" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Plug size={18} className="text-coach-blue" />
      <div className="font-display text-[16px] text-deep-road-navy">{title}</div>
      <div className="flex-1" />
      {badge}
    </div>
    <div className="text-[13px] text-steel mt-3">{description}</div>
    <div className="flex gap-2 mt-4">
      {actions.map((a) => (
        <button key={a.label} onClick={a.onClick} data-testid={a.testid}
          className={a.primary
            ? "bg-coach-blue text-white text-[12.5px] font-medium px-3 py-2 rounded-sm"
            : "border border-coach-silver text-carbon text-[12.5px] px-3 py-2 rounded-sm hover:border-coach-blue hover:text-coach-blue"
          }>
          {a.primary && <PlugZap size={12} className="inline mr-1" />}
          {a.label}
        </button>
      ))}
    </div>
  </div>
);

const StatusBadge = ({ variant, children }) => {
  const cls = variant === "demo" ? "border-amber-hold text-amber-hold" : "border-steel text-steel";
  return <span className={`inline-block px-2 py-0.5 border rounded-sm text-[10.5px] uppercase tracking-widest ${cls}`}>{children}</span>;
};
